/**
 * The words for our Table 12 against the client's own GSTR-1 HSN summary (W3-12 (4a)).
 *
 * `PROBLEM_WORDS` is keyed on the codes `app/ingestion/client_gstr1.check` writes, the
 * ones GSTN's Excel layout adds (`gstn_workbook.check_tables`, D-72) and the one ProGST's own
 * utility workbook adds (`utility_workbook.check_unread`, W3-12 (4b-2));
 * `tests/client-gstr1-check.test.ts` holds it to all three lists, so a new code cannot reach
 * the screen as a bare slug.
 */

import type { ClientGstr1OwnFile, ClientGstr1Row } from './api'

/** What each of the summary's own problems means, in a reviewer's words. */
export const PROBLEM_WORDS: Record<string, (p: Record<string, unknown>) => string> = {
  code_not_in_directory: (p) =>
    `${String(p.hsn)} (${String(p.section).toUpperCase()}, ${String(p.rate)}%) is not a code GSTN ` +
    `publishes${Array.isArray(p.under) && p.under.length > 0 ? ` — codes under it: ${(p.under as string[]).join(', ')}` : ''}.`,
  subtotal_differs: (p) =>
    `The ${String(p.section).toUpperCase()} rows do not add up to the summary's own sub-total.`,
  total_differs: () => "The sections do not add up to the summary's own total.",
  rate_not_a_slab: (p) => `${String(p.hsn)} is listed at ${String(p.rate)}%, which is not a GST rate.`,
  tax_differs: (p) =>
    `${String(p.hsn)} at ${String(p.rate)}%: the tax listed is not taxable × rate.`,
  gstin_differs: (p) =>
    `The summary is for ${String(p.summary)}, not this client (${String(p.client)}).`,
  period_differs: (p) => `The summary is for ${String(p.summary)}, not ${String(p.period)}.`,
  sheet_total_differs: (p) =>
    `${SHEET_WORDS[String(p.sheet)] ?? String(p.sheet)}: the rows give ${amount(String(p.field), Number(p.rows))} ` +
    `for ${(FIELD_WORDS[String(p.field)] ?? String(p.field)).toLowerCase()}, the sheet's own total says ` +
    `${amount(String(p.field), Number(p.stated))}.`,
  service_uqc: (p) =>
    `${String(p.hsn)} is a service code listed with the unit ${String(p.uqc)} — GSTN expects NA for services, ` +
    `and the portal rejects anything else.`,
  register_total_differs: (p) =>
    `The register sheet's rows add up to ₹${rupees(Number(p.rows))} taxable; its own total row says ` +
    `₹${rupees(Number(p.stated))}.`,
  register_differs: (p) =>
    `The register sheet comes to ₹${rupees(Number(p.register))} taxable, net of credit notes; the ` +
    `return's tables come to ₹${rupees(Number(p.tables))}.`,
  sheet_unread: (p) =>
    p.rows === null || p.rows === undefined
      ? `The ${String(p.sheet)} sheet is missing from the workbook, so nothing of it was read.`
      : `The ${String(p.sheet)} sheet has ${Number(p.rows).toLocaleString('en-IN')} row` +
        `${Number(p.rows) === 1 ? '' : 's'} ProGST does not read back, so ${Number(p.rows) === 1 ? 'it is' : 'they are'} ` +
        `not in this comparison.`,
}

/**
 * W3-12 (4b-2), P5: the upload is a workbook ProGST generated, byte for byte — not the client's.
 * Said in place of a comparison, which would only set our draft beside itself.
 */
export function ownFileWords(own: ClientGstr1OwnFile): string {
  const which = [
    own.version !== null ? `version ${own.version}` : null,
    own.period ? `for ${own.period}` : null,
  ]
    .filter(Boolean)
    .join(', ')
  return (
    `${own.filename ?? 'This upload'} is ProGST's own GSTR-1 utility workbook${which ? ` (${which})` : ''}, ` +
    `not the client's. Nothing is compared with it: set beside the draft it was written from, it ` +
    `could only agree. Upload the GSTR-1 the client's software made.`
  )
}

/** The return's tables, as GSTR-1 numbers them. */
export const TABLE_WORDS: Record<string, string> = {
  b2b: 'B2B invoices (4)',
  b2cl: 'B2C large invoices (5)',
  b2cs: 'B2C others (7)',
  exp: 'Exports (6A)',
  at: 'Advances received (11A)',
  atadj: 'Advances adjusted (11B)',
  cdnr: 'Notes to registered persons (9B)',
  cdnur: 'Notes to unregistered persons (9B)',
  nil: 'Nil rated, exempted, non-GST (8)',
  docs: 'Documents issued (13)',
}

/** A sheet of GSTN's layout, by the table it carries. */
export const SHEET_WORDS: Record<string, string> = {
  ...TABLE_WORDS,
  at: 'Advances received (11A)',
  atadj: 'Advances adjusted (11B)',
  exemp: TABLE_WORDS.nil!,
  hsn: 'HSN summary (12)',
  hsn_b2b: 'HSN summary, B2B (12)',
  hsn_b2c: 'HSN summary, B2C (12)',
}

/** A row of Table 8 or Table 13. */
export const ROW_WORDS: Record<string, string> = {
  inter_registered: 'Inter-State, to registered persons',
  intra_registered: 'Intra-State, to registered persons',
  inter_unregistered: 'Inter-State, to unregistered persons',
  intra_unregistered: 'Intra-State, to unregistered persons',
  INV: 'Invoices for outward supply',
  CRN: 'Credit notes',
  DBN: 'Debit notes',
}

/** What a table is compared on. Counts are counts; everything else is money in paise. */
export const FIELD_WORDS: Record<string, string> = {
  documents: 'Documents',
  recipients: 'Recipients',
  codes: 'Codes',
  count: 'Issued',
  cancelled: 'Cancelled',
  taxable: 'Taxable value',
  cess: 'Cess',
  doc_value: 'Document value',
  value: 'Total value',
  advance: 'Advance',
  nil_rated: 'Nil rated',
  exempt: 'Exempted',
  non_gst: 'Non-GST',
}

const COUNTS = new Set(['documents', 'recipients', 'codes', 'count', 'cancelled'])

/** A figure as its field reads: a count as a number, money in rupees. */
export function amount(field: string, value: number): string {
  return COUNTS.has(field) ? value.toLocaleString('en-IN') : `₹${rupees(value)}`
}

/** A difference as its field reads; none is a dash. */
export function signedAmount(field: string, value: number): string {
  if (!COUNTS.has(field)) return signed(value)
  if (value === 0) return '—'
  return `${value > 0 ? '+' : '−'}${Math.abs(value).toLocaleString('en-IN')}`
}

export function rupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function signed(paise: number): string {
  if (paise === 0) return '—'
  return `${paise > 0 ? '+' : '−'}${rupees(Math.abs(paise))}`
}

export function rowWords(row: ClientGstr1Row): string {
  if (row.ok) return 'Agrees'
  if (row.only === 'ours') return 'Only in ours'
  if (row.only === 'theirs') return "Only in the client's"
  return 'Differs'
}

